import { useMergeGoalLinkChanges, useUpdateGoal } from 'features/goals';
import { useUpdateLifeArea } from 'features/life-areas';
import { useCallback } from 'react';
import { rootId } from 'shared/components/ui/virtualized-tree-list';
import {
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  UpdateGoal,
} from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { GoalSortingMode } from 'shared/types/sorting-mode';

// Parallel to useSubGoalReorder / useGoalOverviewReorder: persists to the life area's goalSorting.
export const useLifeAreaGoalReorder = (lifeAreaId: ID) => {
  const { submit: updateGoal } = useUpdateGoal();
  const mergeGoalLinkChanges = useMergeGoalLinkChanges();
  const { submit: updateLifeArea } = useUpdateLifeArea();

  return useCallback(
    (
      updatedSections: { id: ID; goalSorting: ID[] }[],
      updatedGoals: UpdateGoal[],
      goalLinkChanges: GoalLifeAreaLinkChange[] = [],
      goalParentChanges: GoalParentLinkChange[] = [],
    ) => {
      // The list's root is this life area: a goal un-nested to top level links to the area instead.
      const reattached = goalParentChanges
        .filter(({ parentIds }) => !parentIds.length)
        .map(({ goalId }) => ({ goalId, lifeAreaIds: [lifeAreaId] }));

      mergeGoalLinkChanges(updatedGoals, {
        goalLifeAreaChanges: [...goalLinkChanges, ...reattached],
        goalParentChanges,
      }).forEach((goal) => updateGoal(goal));

      updatedSections.forEach(({ id, goalSorting }) => {
        updateLifeArea({
          id: id === rootId ? lifeAreaId : id,
          goalSorting,
          goalSortingMode: GoalSortingMode.Custom,
        });
      });
    },
    [mergeGoalLinkChanges, lifeAreaId, updateGoal, updateLifeArea],
  );
};
